import { useState, useEffect } from "react";
import { Form, Button, Spinner, Dropdown, Badge } from "react-bootstrap";
import { ArrowLeft, Plus, Camera, Sparkles, Wand2, ChefHat, Image as ImageIcon, Save, X, Users, Lock, Crown, GripVertical, Link, Clock, Utensils, CheckSquare } from "lucide-react";
import { Tag } from "lucide-react";
import PlanSelectionModal from "../Billing/PlanSelectionModal";
import { generateHeroShotApi, generateStepImageApi, generateRecipeFromUrlApi } from "../../services/recipeService";
import { compressImage } from "../../utils/imageCompression";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * Sortable Step Item Component
 */
const SortableStep = ({ step, index, steps, updateStepText, removeStep, handleStepMediaChange, handleGenerateStepImage, updateIngredient, removeIngredient, addIngredientToStep, insertStep, updateDependency, updateStepDuration }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: step.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const availableDependencyOptions = steps
        .filter(s => s.stepNumber !== step.stepNumber) // Can't depend on self
        .map(s => ({
            value: s.stepNumber,
            label: `Schritt ${s.stepNumber}`
        }));

    return (
        <div ref={setNodeRef} style={style} className="position-relative mb-5">
            <div className="d-flex align-items-center mb-2 gap-2">
                <div {...attributes} {...listeners} className="cursor-grab text-muted hover-text-dark p-1">
                    <GripVertical size={24} />
                </div>

                <h5 className="font-serif text-muted mb-0">Schritt {index + 1}</h5>

                <div className="d-flex align-items-center gap-1 ms-3 border-start ps-3">
                    <Clock size={14} className="text-muted" />
                    <Form.Control
                        type="number"
                        min="0"
                        size="sm"
                        className="bg-light border-0 text-center p-1 rounded"
                        style={{ width: '45px' }}
                        placeholder="Min"
                        value={step.durationSeconds ? Math.floor(step.durationSeconds / 60) : ''}
                        onChange={e => {
                            const val = e.target.value === '' ? '' : parseInt(e.target.value);
                            updateStepDuration(index, val, step.durationSeconds ? (step.durationSeconds % 60) : 0);
                        }}
                    />
                    <small className="text-muted">:</small>
                    <Form.Control
                        type="number"
                        min="0"
                        max="59"
                        size="sm"
                        className="bg-light border-0 text-center p-1 rounded"
                        style={{ width: '45px' }}
                        placeholder="Sek"
                        value={step.durationSeconds ? (step.durationSeconds % 60) : ''}
                        onChange={e => {
                            const val = e.target.value === '' ? '' : parseInt(e.target.value);
                            updateStepDuration(index, step.durationSeconds ? Math.floor(step.durationSeconds / 60) : 0, val);
                        }}
                    />
                </div>

                <button
                    className="btn btn-outline-danger btn-sm rounded-circle ms-auto"
                    onClick={() => removeStep(index)}
                    title="Schritt entfernen"
                    style={{ width: '32px', height: '32px', padding: 0 }}
                    disabled={steps.length === 1}
                >
                    <X size={16} />
                </button>
            </div>

            <div className="card shadow-sm border-light rounded-4 bg-white overflow-hidden">
                {/* Top: Ingredients (Horizontal) */}
                <div className="bg-light bg-opacity-50 p-3 border-bottom d-flex flex-column gap-2">
                    <div className="d-flex align-items-center justify-content-between">
                        <h6 className="small fw-bold text-uppercase text-muted mb-0 d-flex align-items-center gap-2">
                            <ChefHat size={14} /> Zutaten für diesen Schritt
                        </h6>
                        <button
                            className="btn btn-link btn-sm p-0 text-primary fw-bold text-decoration-none d-flex align-items-center gap-1"
                            onClick={() => addIngredientToStep(index)}
                        >
                            <Plus size={14} /> Zutat
                        </button>
                    </div>

                    <div className="d-flex flex-wrap gap-2">
                        {step.ingredients.length > 0 ? (
                            step.ingredients.map(ing => (
                                <div key={ing.id} className="bg-white border rounded-pill px-2 py-1 d-flex align-items-center gap-2 shadow-sm" style={{ maxWidth: '100%' }}>
                                    <Form.Control
                                        placeholder="Menge"
                                        className="bg-transparent border-0 text-center p-0 fw-bold"
                                        style={{ width: '40px', fontSize: '0.85rem', boxShadow: 'none' }}
                                        value={ing.amount}
                                        onChange={(e) => updateIngredient(index, ing.id, 'amount', e.target.value)}
                                    />
                                    <Form.Control
                                        placeholder="Einh."
                                        className="bg-transparent border-0 text-center p-0 text-muted"
                                        style={{ width: '35px', fontSize: '0.85rem', boxShadow: 'none' }}
                                        value={ing.unit}
                                        onChange={(e) => updateIngredient(index, ing.id, 'unit', e.target.value)}
                                    />
                                    <Form.Control
                                        placeholder="Zutat"
                                        className="bg-transparent border-0 p-0 fw-medium"
                                        style={{ width: '100px', fontSize: '0.85rem', boxShadow: 'none' }}
                                        value={ing.name}
                                        onChange={(e) => updateIngredient(index, ing.id, 'name', e.target.value)}
                                    />
                                    <button
                                        className="btn btn-link text-danger p-0 ms-1 opacity-50 hover-opacity-100"
                                        onClick={() => removeIngredient(index, ing.id)}
                                        tabIndex={-1}
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div className="text-muted small fst-italic py-1 px-2">Keine Zutaten für diesen Schritt.</div>
                        )}
                    </div>
                </div>

                <div className="p-3">
                    <div className="row g-4">
                        {/* Dependency & Media (Right Column now Left to balance?) or keep as is but without ingredients */}
                        {/* Let's put Media on right like in view or keep side-by-side */}
                        {/* User wants efficiency. Let's keep Text on Left (larger) and Media/Meta on Right (smaller) */}

                        <div className="col-md-8">
                            <div className="form-floating h-100">
                                <Form.Control
                                    as="textarea"
                                    placeholder={`Beschreibe Schritt ${index + 1}...`}
                                    className="form-control h-100 fs-6 font-sans border-0 shadow-none bg-light"
                                    style={{ minHeight: '180px', resize: 'none' }}
                                    value={step.text}
                                    onChange={(e) => updateStepText(index, e.target.value)}
                                />
                                <label className="text-muted">Anweisung</label>
                            </div>
                        </div>

                        <div className="col-md-4">
                            {/* Dependency */}
                            <div className="mb-2">
                                <Dropdown>
                                    <Dropdown.Toggle variant="outline-light" size="sm" className="w-100 text-start text-muted border d-flex justify-content-between align-items-center">
                                        <span className="text-truncate small">
                                            <Link size={12} className="me-1" />
                                            {step.dependsOnStepNumbers && step.dependsOnStepNumbers.length > 0
                                                ? `Hängt ab von ${step.dependsOnStepNumbers.join(", ")}`
                                                : "Keine Abhängigkeiten"}
                                        </span>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu className="w-100 shadow-sm border-0">
                                        {availableDependencyOptions.length > 0 ? availableDependencyOptions.map(opt => (
                                            <Dropdown.Item
                                                key={opt.value}
                                                active={step.dependsOnStepNumbers?.includes(opt.value)}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    updateDependency(index, opt.value);
                                                }}
                                            >
                                                <Form.Check
                                                    type="checkbox"
                                                    label={opt.label}
                                                    checked={step.dependsOnStepNumbers?.includes(opt.value) || false}
                                                    readOnly
                                                    className="pointer-events-none"
                                                />
                                            </Dropdown.Item>
                                        )) : <Dropdown.Item disabled>Keine anderen Schritte</Dropdown.Item>}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>

                            {/* Image Upload */}
                            <label className="d-block w-100 cursor-pointer">
                                <input type="file" className="d-none" accept="image/*" onChange={(e) => handleStepMediaChange(index, e)} />
                                <div
                                    className="dashed-placeholder d-flex align-items-center justify-content-center overflow-hidden position-relative rounded-3"
                                    style={{ height: '140px' }}
                                >
                                    {step.mediaPreview ? (
                                        <>
                                            <img
                                                key={step.mediaPreview}
                                                src={step.mediaPreview}
                                                alt={`Step ${index + 1}`}
                                                className="w-100 h-100 object-fit-cover"
                                            />
                                            <button
                                                className="btn btn-warning btn-sm rounded-pill position-absolute bottom-0 end-0 m-2 d-flex align-items-center gap-1 shadow-sm"
                                                onClick={(e) => { e.preventDefault(); handleGenerateStepImage(index); }}
                                                disabled={step.isGenerating}
                                            >
                                                {step.isGenerating ? <Spinner size="sm" animation="border" /> : <Sparkles size={14} />}
                                                KI Neu
                                            </button>
                                        </>
                                    ) : (
                                        <div className="text-center text-muted small">
                                            <ImageIcon size={20} className="mb-1 opacity-50" />
                                            <div>Bild</div>
                                            <button
                                                className="btn btn-warning btn-sm rounded-pill mt-1 d-inline-flex align-items-center gap-1"
                                                onClick={(e) => { e.preventDefault(); handleGenerateStepImage(index); }}
                                                disabled={step.isGenerating}
                                            >
                                                {step.isGenerating ? <Spinner size="sm" animation="border" /> : <Sparkles size={14} />}
                                                KI
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {/* Insert In Between Button */}
            <div className="position-absolute start-50 translate-middle-x" style={{ bottom: '-30px', zIndex: 5 }}>
                <button
                    className="btn btn-sm btn-light border shadow-sm rounded-circle p-2 hover-scale text-primary"
                    onClick={() => insertStep(index + 1)}
                    title="Schritt danach einfügen"
                >
                    <Plus size={20} />
                </button>
            </div>
        </div>
    );
};

/**
 * Step-Centric Recipe Editor
 * Visual Style aligned with RecipeView (Modern, Clean, Serif/Sans mix)
 */
const RecipeEditDialog = ({ onBack, onSave, onOptimize, initialData, isPro, token }) => {
    const [saving, setSaving] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [isOptimizing, setIsOptimizing] = useState(false);
    const [optimizedData, setOptimizedData] = useState(null);
    const [originalData, setOriginalData] = useState(null);
    const [errors, setErrors] = useState({});

    const [generalInfo, setGeneralInfo] = useState({
        name: "",
        description: "",
        servings: 2,
        prepTime: 0,
        cookTime: 0,
        difficulty: 'medium',
        heroImage: null,
        heroPreview: null,
        heroS3Key: null,
        tags: []
    });

    const [steps, setSteps] = useState([
        { id: Date.now(), stepNumber: 1, text: "", ingredients: [], mediaFile: null, mediaPreview: null, mediaS3Key: null, dependsOnStepNumbers: [] }
    ]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // --- Initialization ---
    useEffect(() => {
        // Reset state only when initialData changes (new access)
        setIsOptimizing(false);
        setOptimizedData(null);
        setOriginalData(null);

        if (initialData) {
            // Find Hero Media Logic
            const heroMedia = initialData.media?.find(m => m.isHeroShot) || initialData.media?.[0] || null;

            setGeneralInfo({
                name: initialData.title || "",
                description: initialData.description || "",
                servings: initialData.servings || 2,
                prepTime: initialData.prepTime || 0,
                cookTime: initialData.cookTime || 0,
                difficulty: initialData.difficulty || 'medium',
                heroImage: null,
                heroPreview: heroMedia?.url || null,
                heroS3Key: heroMedia?.s3Key || null,
                tags: (initialData.tags || []).map(t => t.name || t)
            });

            const loadedSteps = (initialData.steps && initialData.steps.length > 0)
                ? initialData.steps.map((s, idx) => {
                    const stepIngredients = (initialData.ingredients || [])
                        .filter(i => {
                            // Match by ID (preferred)
                            if (s.id && i.stepId && String(i.stepId) === String(s.id)) return true;
                            // Match by Step Number (fallback, strict)
                            const sNum = s.stepNumber || s.step_number;
                            const iNum = i.stepNumber || i.step_number;
                            if (sNum && iNum && Number(sNum) === Number(iNum)) return true;
                            return false;
                        })
                        .map(i => ({
                            id: i.id || Date.now() + Math.random(),
                            name: i.name,
                            amount: i.quantity,
                            unit: i.unit,
                            isOptional: i.isOptional || false
                        }));

                    const stepMedia = (initialData.media || [])
                        .find(m => {
                            if (m.isHeroShot) return false;
                            // Match by ID
                            if (s.id && m.stepId && String(m.stepId) === String(s.id)) return true;
                            // Match by Step Number (Strict)
                            const sNum = s.stepNumber || s.step_number;
                            const mNum = m.stepNumber || m.step_number;
                            if (sNum && mNum && Number(sNum) === Number(mNum)) return true;
                            return false;
                        });

                    return {
                        id: s.id || Date.now() + idx,
                        stepNumber: idx + 1,
                        text: s.instruction,
                        durationSeconds: s.durationSeconds || s.duration_seconds,
                        dependsOnStepNumbers: s.dependsOnStepNumbers || s.depends_on_step_numbers || [],
                        ingredients: stepIngredients,
                        mediaFile: null,
                        mediaPreview: stepMedia?.url || null,
                        mediaS3Key: stepMedia?.s3Key || null
                    };
                })
                : [{ id: Date.now(), stepNumber: 1, text: "", ingredients: [], mediaFile: null, mediaPreview: null, mediaS3Key: null, dependsOnStepNumbers: [] }];

            setSteps(loadedSteps);
        } else {
            setGeneralInfo({ name: "", description: "", servings: 2, prepTime: 0, cookTime: 0, heroImage: null, heroPreview: null, heroS3Key: null, tags: [] });
            setSteps([{ id: Date.now(), stepNumber: 1, text: "", ingredients: [], mediaFile: null, mediaPreview: null, mediaS3Key: null, dependsOnStepNumbers: [] }]);
        }
    }, [initialData]);

    // Locked State Logic
    // Lock if published AND (pending OR approved). 
    // If rejected, allow editing (which implicitly unpublishes or stays published? Backend says "Changes not allowed" if locked. 
    // Backend update logic: if published AND (pending or approved). So if rejected, update is allowed.
    // We should probably allow editing if rejected.
    const isLocked = initialData?.publicationDate && (initialData?.editorialStatus === 'pending' || initialData?.editorialStatus === 'approved');
    const canEdit = !isLocked;

    // --- General Handlers ---
    const handleGeneralChange = (field, value) => {
        setGeneralInfo(prev => ({ ...prev, [field]: value }));
    };

    const handleAddTag = (e) => {
        if (e.key === 'Enter' || e.type === 'click') {
            e.preventDefault();
            const val = e.target.value || document.getElementById('tag-input').value;
            if (val && val.trim()) {
                const newTag = val.trim();
                if (!generalInfo.tags.includes(newTag)) {
                    setGeneralInfo(prev => ({ ...prev, tags: [...prev.tags, newTag] }));
                }
                if (document.getElementById('tag-input')) document.getElementById('tag-input').value = '';
            }
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setGeneralInfo(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tagToRemove) }));
    };

    const handleHeroImageChange = async (e) => {
        if (e.target.files && e.target.files[0]) {
            const originalFile = e.target.files[0];

            // Cleanup old preview if it was a blob
            if (generalInfo.heroPreview && generalInfo.heroPreview.startsWith('blob:')) {
                URL.revokeObjectURL(generalInfo.heroPreview);
            }

            // Create temporary preview immediately for responsiveness
            const tempPreview = URL.createObjectURL(originalFile);
            setGeneralInfo(prev => ({
                ...prev,
                heroPreview: tempPreview
            }));

            try {
                // Compress
                const compressedFile = await compressImage(originalFile);

                // Update with compressed file
                setGeneralInfo(prev => ({
                    ...prev,
                    heroImage: compressedFile
                }));
            } catch (err) {
                console.error("Compression failed, using original", err);
                setGeneralInfo(prev => ({
                    ...prev,
                    heroImage: originalFile
                }));
            }
        }
    };

    const addStep = () => {
        setSteps(prev => [...prev, { id: Date.now(), stepNumber: prev.length + 1, text: "", ingredients: [], mediaFile: null, mediaPreview: null, mediaS3Key: null, dependsOnStepNumbers: [] }]);
    };

    const insertStep = (atIndex) => {
        setSteps(prev => {
            const newStep = { id: Date.now(), stepNumber: atIndex + 1, text: "", ingredients: [], mediaFile: null, mediaPreview: null, mediaS3Key: null, dependsOnStepNumbers: [] };
            const newSteps = [...prev];
            newSteps.splice(atIndex, 0, newStep);
            // Re-index stepNumbers
            return newSteps.map((s, i) => ({ ...s, stepNumber: i + 1 }));
        });
    }

    const removeStep = (index) => {
        if (!canEdit) return;
        if (steps.length === 1) return;
        setSteps(prev => {
            const newSteps = prev.filter((_, i) => i !== index);
            return newSteps.map((s, i) => ({ ...s, stepNumber: i + 1 }));
        });
    };

    const updateStepText = (index, text) => {
        if (!canEdit) return;
        setSteps(prev => prev.map((s, i) => i === index ? { ...s, text } : s));
    };

    const updateStepDuration = (index, minutes, seconds) => {
        if (!canEdit) return;
        const mins = parseInt(minutes) || 0;
        const secs = parseInt(seconds) || 0;
        const totalSeconds = (mins * 60) + secs;

        setSteps(prev => prev.map((s, i) => i === index ? { ...s, durationSeconds: totalSeconds > 0 ? totalSeconds : null } : s));
    };

    const updateDependency = (index, targetStepNum) => {
        if (!canEdit) return;
        setSteps(prev => prev.map((s, i) => {
            if (i === index) {
                const currentDeps = s.dependsOnStepNumbers || [];
                const newDeps = currentDeps.includes(targetStepNum)
                    ? currentDeps.filter(d => d !== targetStepNum)
                    : [...currentDeps, targetStepNum];
                return { ...s, dependsOnStepNumbers: newDeps };
            }
            return s;
        }));
    };

    const handleStepMediaChange = async (index, e) => {
        if (!canEdit) return;
        if (e.target.files && e.target.files[0]) {
            const originalFile = e.target.files[0];

            // Immediate preview update
            const tempPreview = URL.createObjectURL(originalFile);

            setSteps(prev => prev.map((s, i) => {
                if (i === index) {
                    if (s.mediaPreview && s.mediaPreview.startsWith('blob:')) {
                        URL.revokeObjectURL(s.mediaPreview);
                    }
                    return { ...s, mediaPreview: tempPreview };
                }
                return s;
            }));

            try {
                // Reduce resolution for step images (approx half of hero)
                const compressedFile = await compressImage(originalFile, {
                    maxWidthOrHeight: 960,
                    maxSizeMB: 0.25 // Try to keep it very small for list views
                });
                setSteps(prev => prev.map((s, i) => {
                    if (i === index) {
                        return {
                            ...s,
                            mediaFile: compressedFile,
                        };
                    }
                    return s;
                }));
            } catch (err) {
                console.error("Step image compression failed", err);
                setSteps(prev => prev.map((s, i) => {
                    if (i === index) {
                        return {
                            ...s,
                            mediaFile: originalFile,
                        };
                    }
                    return s;
                }));
            }
        }
    };

    const addIngredientToStep = (stepIndex) => {
        if (!canEdit) return;
        setSteps(prev => prev.map((s, i) => {
            if (i === stepIndex) {
                return {
                    ...s,
                    ingredients: [...s.ingredients, { id: Date.now(), name: "", amount: "", unit: "" }]
                };
            }
            return s;
        }));
    };

    const updateIngredient = (stepIndex, ingId, field, value) => {
        if (!canEdit) return;
        setSteps(prev => prev.map((s, i) => {
            if (i === stepIndex) {
                return {
                    ...s,
                    ingredients: s.ingredients.map(ing =>
                        ing.id === ingId ? { ...ing, [field]: value } : ing
                    )
                };
            }
            return s;
        }));
    };

    const removeIngredient = (stepIndex, ingId) => {
        if (!canEdit) return;
        setSteps(prev => prev.map((s, i) => {
            if (i === stepIndex) {
                return {
                    ...s,
                    ingredients: s.ingredients.filter(ing => ing.id !== ingId)
                };
            }
            return s;
        }));
    };

    // --- Drag and Drop ---
    const handleDragEnd = (event) => {
        if (!canEdit) return;
        const { active, over } = event;

        if (active.id !== over.id) {
            setSteps((items) => {
                const oldIndex = items.findIndex(i => i.id === active.id);
                const newIndex = items.findIndex(i => i.id === over.id);

                const newItems = arrayMove(items, oldIndex, newIndex);
                // Re-index stepNumbers
                return newItems.map((s, i) => ({ ...s, stepNumber: i + 1 }));
            });
        }
    };

    // --- Save Logic ---
    const getRecipeDataFromState = () => {
        const allIngredients = [];
        steps.forEach((step, idx) => {
            step.ingredients.forEach(ing => {
                if (ing.name) {
                    allIngredients.push({
                        name: ing.name,
                        quantity: parseFloat(ing.amount) || 0,
                        unit: ing.unit || "",
                        stepNumber: idx + 1,
                        isOptional: ing.isOptional
                    });
                }
            });
        });

        const allSteps = steps
            .filter(s => s.text)
            .map((s, idx) => ({
                instruction: s.text,
                stepNumber: idx + 1,
                durationSeconds: s.durationSeconds,
                dependsOnStepNumbers: s.dependsOnStepNumbers
            }));

        // Media Construction
        const keptMedia = [];

        // Hero Image
        if (!generalInfo.heroImage && generalInfo.heroPreview) {
            keptMedia.push({
                url: generalInfo.heroS3Key || generalInfo.heroPreview, // Use Key if available to prevent signing loop
                isHeroShot: true,
                mediaType: 'image'
            });
        }

        // Step Media
        steps.forEach((step, idx) => {
            if (!step.mediaFile && step.mediaPreview) {
                keptMedia.push({
                    url: step.mediaS3Key || step.mediaPreview, // Use Key if available
                    stepNumber: idx + 1,
                    isHeroShot: false,
                    mediaType: 'image'
                });
            }
        });

        return {
            title: generalInfo.name,
            description: generalInfo.description,
            servings: parseInt(generalInfo.servings) || 2,
            languageCode: "de",
            prepTime: parseInt(generalInfo.prepTime) || 0,
            cookTime: parseInt(generalInfo.cookTime) || 0,
            difficulty: generalInfo.difficulty,
            ingredients: allIngredients,
            steps: allSteps,
            media: keptMedia,
            tags: generalInfo.tags
        };
    };

    const handleSave = async (closeAfterSave = true) => {
        if (isLocked) {
            alert("Änderungen nicht möglich. Das Rezept ist veröffentlicht und gesperrt.");
            return;
        }

        // Validation
        const newErrors = {};
        if (!generalInfo.name.trim()) newErrors.name = true;

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        setErrors({});
        setSaving(true);
        try {
            const recipeData = getRecipeDataFromState();

            const formData = new FormData();
            formData.append("json", JSON.stringify(recipeData));

            if (generalInfo.heroImage) {
                // Fix for iOS: explicit filename
                formData.append("image", generalInfo.heroImage, generalInfo.heroImage.name || "hero_image.jpg");
            }

            steps.forEach((step, idx) => {
                if (step.mediaFile) {
                    // Fix for iOS: explicit filename
                    formData.append(`step_${idx + 1}`, step.mediaFile, step.mediaFile.name || `step_${idx + 1}.jpg`);
                }
            });

            let savedResult = null;
            if (onSave) {
                savedResult = await onSave(formData, !closeAfterSave);
            }

            if (closeAfterSave) {
                onBack();
            }
            return savedResult;

        } catch (error) {
            console.error("Failed to save recipe", error);
            alert("Fehler beim Speichern: " + (error.message || "Unbekannter Fehler"));
        } finally {
            setSaving(false);
        }
    };

    const [prompt, setPrompt] = useState("");

    // --- Shared helper: generate step images in background with per-step loading state ---
    const generateStepImagesInBackground = (stepsToProcess, title, description) => {
        // Mark all steps that have text as generating
        setSteps(prev => prev.map((s, i) =>
            stepsToProcess[i]?.text?.trim() ? { ...s, isGenerating: true } : s
        ));

        stepsToProcess.forEach((step, idx) => {
            if (!step.text?.trim()) return;

            const stepIngredients = (step.ingredients || [])
                .filter(i => i.name?.trim())
                .map(i => `${i.amount} ${i.unit} ${i.name}`.trim())
                .join(", ");

            const cumulativeIngredients = stepsToProcess
                .slice(0, idx + 1)
                .flatMap(s => s.ingredients || [])
                .filter(i => i.name?.trim())
                .map(i => `${i.amount} ${i.unit} ${i.name}`.trim())
                .join(", ");

            generateStepImageApi({
                token,
                title,
                description,
                stepNumber: idx + 1,
                stepInstruction: step.text,
                stepIngredients,
                cumulativeIngredients,
                imageSize: "1024x1024",
            }).then(blob => {
                if (blob?.size > 0) {
                    const file = new File([blob], `ai_step_${idx + 1}.png`, { type: "image/png" });
                    setSteps(prev => prev.map((s, i) =>
                        i === idx ? { ...s, mediaFile: file, mediaPreview: URL.createObjectURL(file), isGenerating: false } : s
                    ));
                } else {
                    setSteps(prev => prev.map((s, i) =>
                        i === idx ? { ...s, isGenerating: false } : s
                    ));
                }
            }).catch(() => {
                setSteps(prev => prev.map((s, i) =>
                    i === idx ? { ...s, isGenerating: false } : s
                ));
            });
        });
    };

    // --- Optimization Logic ---
    const handleOptimize = async () => {
        // Detect URL in prompt — use url generation flow
        const urlMatch = prompt.match(/https?:\/\/[^\s]+/);
        if (urlMatch) {
            const url = urlMatch[0];
            const instructions = prompt.replace(url, "").trim();

            setIsOptimizing(true);
            try {
                const result = await generateRecipeFromUrlApi({ token, url, instructions });
                if (!result) throw new Error("No result from recipe generator");

                setGeneralInfo(prev => ({
                    ...prev,
                    name: result.title || "",
                    description: result.description || "",
                    servings: result.servings || 2,
                    prepTime: result.prepTime || 0,
                    cookTime: result.cookTime || 0,
                    tags: result.tags || []
                }));

                const newSteps = (result.steps || []).map((s, idx) => {
                    const stepNum = s.stepNumber || idx + 1;
                    const stepIngredients = (result.ingredients || [])
                        .filter(i => Number(i.stepNumber) === Number(stepNum))
                        .map(i => ({
                            id: Date.now() + Math.random(),
                            name: i.name,
                            amount: i.quantity,
                            unit: i.unit
                        }));
                    return {
                        id: Date.now() + idx,
                        stepNumber: stepNum,
                        text: s.instruction,
                        durationSeconds: s.durationSeconds,
                        dependsOnStepNumbers: s.dependsOnStepNumbers || [],
                        ingredients: stepIngredients,
                        mediaFile: null,
                        mediaPreview: null,
                        mediaS3Key: null
                    };
                });
                setSteps(newSteps);

                // Auto hero shot (blocking)
                try {
                    const allIngredients = (result.ingredients || []).map(i => `${i.quantity} ${i.unit} ${i.name}`).join(", ");
                    const allStepsText = (result.steps || []).map(s => s.instruction).filter(Boolean).join(" ");
                    const imageBlob = await generateHeroShotApi({
                        token,
                        title: result.title,
                        description: result.description,
                        ingredients: allIngredients,
                        steps: allStepsText
                    });
                    if (imageBlob?.size > 0) {
                        const file = new File([imageBlob], "ai_hero_shot.png", { type: "image/png" });
                        setGeneralInfo(prev => ({
                            ...prev,
                            heroPreview: URL.createObjectURL(file),
                            heroImage: file
                        }));
                    }
                } catch (imgErr) {
                    console.warn("Hero shot failed during import:", imgErr.message);
                }

                // Step images in background — per-step loading indicators, with ingredient context
                generateStepImagesInBackground(newSteps, result.title, result.description);

            } catch (e) {
                console.error("Import failed", e);
                alert("Import fehlgeschlagen. Bitte prüfe die URL und versuche es erneut.");
            } finally {
                setIsOptimizing(false);
            }
            return; // Skip the regular optimize flow
        }

        if (!canEdit) {
            alert("Optimierung nicht möglich. Das Rezept ist veröffentlicht und gesperrt.");
            return;
        }
        if (!generalInfo.name.trim()) {
            setErrors(prev => ({ ...prev, name: true }));
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        setIsOptimizing(true);
        const recipeData = getRecipeDataFromState();

        try {
            if (onOptimize) {
                const optimizedResult = await onOptimize(recipeData, prompt);

                if (!optimizedResult) {
                    console.error("Optimized result is null or undefined");
                    return;
                }

                setOriginalData({ general: { ...generalInfo }, steps: [...steps] });
                setOptimizedData(optimizedResult);

                // --- Media Preservation Logic ---
                setGeneralInfo(prev => ({
                    ...prev,
                    heroPreview: prev.heroPreview,
                    heroImage: prev.heroImage,
                    heroS3Key: prev.heroS3Key,
                    name: optimizedResult.title || prev.name,
                    description: optimizedResult.description || prev.description,
                    servings: optimizedResult.servings || prev.servings,
                    prepTime: optimizedResult.prepTime || prev.prepTime,
                    cookTime: optimizedResult.cookTime || prev.cookTime,
                    tags: optimizedResult.tags || prev.tags
                }));

                const optimizedSteps = (optimizedResult.steps && optimizedResult.steps.length > 0)
                    ? optimizedResult.steps.map((s, idx) => {
                        const currentStepNum = s.step_number || s.stepNumber || idx + 1;

                        const stepIngredients = (optimizedResult.ingredients || [])
                            .filter(i => {
                                if (i.step_number && Number(i.step_number) === Number(currentStepNum)) return true;
                                if (i.stepNumber && Number(i.stepNumber) === Number(currentStepNum)) return true;
                                return false;
                            })
                            .map(i => ({
                                id: i.id || Date.now() + Math.random(),
                                name: i.name,
                                amount: i.quantity,
                                unit: i.unit
                            }));

                        const existingStep = steps[idx];

                        return {
                            id: s.id || (existingStep ? existingStep.id : Date.now() + idx),
                            stepNumber: currentStepNum,
                            text: s.instruction,
                            durationSeconds: s.durationSeconds,
                            dependsOnStepNumbers: s.dependsOnStepNumbers || [],
                            ingredients: stepIngredients,
                            mediaFile: existingStep?.mediaFile || null,
                            mediaPreview: existingStep?.mediaPreview || null,
                            mediaS3Key: existingStep?.mediaS3Key || null
                        };
                    })
                    : steps;

                setSteps(optimizedSteps);
            }
        } catch (e) {
            console.error(e);
            alert("Optimierung fehlgeschlagen.");
        } finally {
            setIsOptimizing(false);
        }
    };

    const handleRejectOptimization = () => {
        if (originalData) {
            setGeneralInfo(originalData.general);
            setSteps(originalData.steps);
        }
        setOptimizedData(null);
        setOriginalData(null);
    };

    const handleAcceptOptimization = () => {
        setOptimizedData(null);
        setOriginalData(null);
    };

    const handleGenerateHeroShot = async () => {
        if (!generalInfo.name.trim()) {
            setErrors(prev => ({ ...prev, name: true }));
            alert("Bitte gib dem Rezept zuerst einen Namen.");
            return;
        }

        setIsOptimizing(true);
        try {
            const allIngredients = steps.flatMap(s => s.ingredients).map(i => `${i.amount} ${i.unit} ${i.name}`).join(", ");
            const allSteps = steps.map(s => s.text).filter(Boolean).join(" ");

            const imageBlob = await generateHeroShotApi({
                token,
                title: generalInfo.name,
                description: generalInfo.description,
                ingredients: allIngredients,
                steps: allSteps
            });

            if (imageBlob && imageBlob.size > 0) {
                const file = new File([imageBlob], "ai_hero_shot.png", { type: "image/png" });
                const previewUrl = URL.createObjectURL(file);

                setGeneralInfo(prev => ({
                    ...prev,
                    heroPreview: previewUrl,
                    heroImage: file // Bind as file for upload
                }));
            }
        } catch (error) {
            console.error("Hero shot generation failed", error);
            alert("Bildgenerierung fehlgeschlagen. Bitte versuche es später erneut.");
        } finally {
            setIsOptimizing(false);
        }
    };

    const handleGenerateStepImage = async (index) => {
        const step = steps[index];
        if (!step.text?.trim()) {
            alert("Bitte gib zuerst eine Anweisung für diesen Schritt ein.");
            return;
        }
        if (!generalInfo.name.trim()) {
            alert("Bitte gib dem Rezept zuerst einen Namen.");
            return;
        }

        setSteps(prev => prev.map((s, i) =>
            i === index ? { ...s, isGenerating: true } : s
        ));

        const stepIngredients = step.ingredients
            .filter(i => i.name?.trim())
            .map(i => `${i.amount} ${i.unit} ${i.name}`.trim())
            .join(", ");

        const cumulativeIngredients = steps
            .slice(0, index + 1)
            .flatMap(s => s.ingredients)
            .filter(i => i.name?.trim())
            .map(i => `${i.amount} ${i.unit} ${i.name}`.trim())
            .join(", ");

        try {
            const imageBlob = await generateStepImageApi({
                token,
                title: generalInfo.name,
                description: generalInfo.description,
                stepNumber: index + 1,
                stepInstruction: step.text,
                stepIngredients,
                cumulativeIngredients,
                imageSize: "1024x1024",
            });

            if (imageBlob?.size > 0) {
                const file = new File([imageBlob], `ai_step_${index + 1}.png`, { type: "image/png" });
                const previewUrl = URL.createObjectURL(file);
                setSteps(prev => prev.map((s, i) =>
                    i === index ? { ...s, mediaFile: file, mediaPreview: previewUrl, isGenerating: false } : s
                ));
            }
        } catch (error) {
            console.error("Step image generation failed", error);
            alert("Bildgenerierung fehlgeschlagen. Bitte versuche es später erneut.");
            setSteps(prev => prev.map((s, i) =>
                i === index ? { ...s, isGenerating: false } : s
            ));
        }
    };

    const [isGeneratingAll, setIsGeneratingAll] = useState(false);

    const handleGenerateAllImages = async () => {
        if (!generalInfo.name.trim()) {
            setErrors(prev => ({ ...prev, name: true }));
            alert("Bitte gib dem Rezept zuerst einen Namen.");
            return;
        }

        setIsGeneratingAll(true);

        // Generate hero shot (blocking)
        try {
            const allIngredients = steps.flatMap(s => s.ingredients).map(i => `${i.amount} ${i.unit} ${i.name}`).join(", ");
            const allSteps = steps.map(s => s.text).filter(Boolean).join(" ");

            const imageBlob = await generateHeroShotApi({
                token,
                title: generalInfo.name,
                description: generalInfo.description,
                ingredients: allIngredients,
                steps: allSteps
            });

            if (imageBlob?.size > 0) {
                const file = new File([imageBlob], "ai_hero_shot.png", { type: "image/png" });
                setGeneralInfo(prev => ({
                    ...prev,
                    heroPreview: URL.createObjectURL(file),
                    heroImage: file
                }));
            }
        } catch (err) {
            console.warn("Hero shot failed during bulk generation:", err.message);
        }

        // Generate step images in background
        generateStepImagesInBackground(steps, generalInfo.name, generalInfo.description);
        setIsGeneratingAll(false);
    };

    return (
        <div className="container-fluid bg-white min-vh-100 py-4 animate-fade-in">
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=Inter:wght@400;500;600&display=swap');
                
                .font-serif { font-family: 'Playfair Display', serif; }
                .font-sans { font-family: 'Inter', sans-serif; }
                .recipe-editor-container { max-width: 900px; margin: 0 auto; }
                
                .form-control-flush {
                    border: none;
                    border-bottom: 2px solid transparent;
                    border-radius: 0;
                    background: transparent;
                    padding-left: 0;
                    transition: border-color 0.2s;
                }
                .form-control-flush:focus {
                    border-bottom-color: var(--bs-primary);
                    background: transparent;
                    box-shadow: none;
                }
                
                .dashed-placeholder {
                    background-color: #f8f9fa;
                    border: 2px dashed #dee2e6;
                    transition: all 0.2s;
                }
                .dashed-placeholder:hover {
                    border-color: var(--bs-primary);
                    background-color: #e9ecef;
                }

                .animate-fade-in { animation: fadeIn 0.4s ease-out; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>

            <div className="recipe-editor-container position-relative">

                {/* --- Top Nav --- */}
                <div className="d-flex align-items-center justify-content-between mb-5 sticky-top bg-white py-2" style={{ zIndex: 100 }}>
                    <button className="btn btn-link text-decoration-none text-muted d-flex align-items-center gap-2 px-0 hover-opacity-75" onClick={onBack}>
                        <ArrowLeft size={20} /> <span className="fw-medium font-sans">Abbrechen</span>
                    </button>

                    <div className="d-flex gap-2">
                        <Button
                            variant="primary"
                            onClick={() => handleSave(true)}
                            className="rounded-pill px-4 fw-medium font-sans shadow-sm d-flex align-items-center gap-2"
                            disabled={saving || isOptimizing}
                        >
                            {saving ? <Spinner size="sm" animation="border" /> : <><Save size={18} /> Speichern</>}
                        </Button>
                    </div>
                </div>

                {isOptimizing && (
                    <div className="position-fixed top-0 start-0 w-100 h-100 bg-white bg-opacity-75 d-flex flex-column align-items-center justify-content-center" style={{ zIndex: 1050, backdropFilter: 'blur(2px)' }}>
                        <Spinner animation="grow" variant="primary" className="mb-3" />
                        <h5 className="fw-bold text-primary animate-pulse font-sans">KI verarbeitet dein Rezept...</h5>
                        <Sparkles className="text-warning mt-2" size={32} />
                    </div>
                )}

                <div className="d-flex flex-column gap-5">
                    {/* Locked Banner */}
                    {isLocked && (
                        <div className="bg-warning bg-opacity-10 border border-warning text-warning p-3 rounded-3 d-flex align-items-center gap-3">
                            <Lock size={24} />
                            <div>
                                <h6 className="fw-bold mb-0">Rezept ist veröffentlicht</h6>
                                <small>Änderungen sind nicht mehr möglich, da das Rezept bereits geprüft und freigegeben wurde.</small>
                            </div>
                        </div>
                    )}

                    {/* --- Optimization Banner --- */}
                    {!optimizedData && !isOptimizing && !isLocked && (
                        <div className="bg-primary bg-opacity-10 rounded-4 p-4 border border-primary border-opacity-10">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="bg-white p-2 rounded-circle shadow-sm text-primary">
                                        <Wand2 size={20} />
                                    </div>
                                    <div className="font-sans">
                                        <h6 className="mb-0 fw-bold text-primary d-flex align-items-center gap-2">
                                            KI-Koch nutzen {!isPro && <Crown size={16} className="text-warning fill-warning" />}
                                        </h6>
                                        <small className="text-muted">Rezepttext optimieren oder alle Bilder generieren.</small>
                                    </div>
                                </div>
                                {isPro ? (
                                    <div className="d-flex gap-2">
                                        <button
                                            className="btn btn-white text-primary fw-bold rounded-pill px-4 shadow-sm border"
                                            onClick={handleOptimize}
                                        >
                                            <Sparkles size={16} className="me-2" fill="currentColor" /> Optimieren
                                        </button>
                                        <button
                                            className="btn btn-outline-primary fw-bold rounded-pill px-4"
                                            onClick={handleGenerateAllImages}
                                            disabled={isGeneratingAll}
                                        >
                                            {isGeneratingAll ? (
                                                <><Spinner size="sm" className="me-2" /> Bilder...</>
                                            ) : (
                                                <><ImageIcon size={16} className="me-2" /> Alle Bilder</>
                                            )}
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        className="btn btn-warning text-white fw-bold rounded-pill px-4 shadow-sm"
                                        onClick={() => setShowUpgradeModal(true)}
                                    >
                                        <Lock size={16} className="me-2" /> Upgrade
                                    </button>
                                )}
                            </div>

                            {isPro ? (
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    placeholder="Anweisungen eingeben (z.B. 'Mach es vegan', 'Mehr Schärfe')"
                                    className="bg-white border-0 shadow-sm rounded-3"
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                />
                            ) : null}
                        </div>
                    )}

                    {optimizedData && (
                        <div className="bg-success bg-opacity-10 rounded-4 p-4 d-flex flex-column gap-3 border border-success border-opacity-25">
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-3">
                                    <Sparkles className="text-success" size={20} />
                                    <h6 className="mb-0 fw-bold text-success font-sans">Vorschlag überprüfen</h6>
                                </div>
                                <div className="d-flex gap-2">
                                    <button className="btn btn-outline-danger btn-sm rounded-pill fw-bold" onClick={handleRejectOptimization}>Ablehnen</button>
                                    <button className="btn btn-success btn-sm rounded-pill fw-bold shadow-sm" onClick={handleAcceptOptimization}>Übernehmen</button>
                                </div>
                            </div>
                        </div>
                    )}


                    {/* --- Section 1: Hero Image & Title --- */}
                    <div className="text-center mb-2">
                        <label className="d-block w-100 cursor-pointer group position-relative mb-4">
                            <input type="file" className="d-none" accept="image/*" onChange={handleHeroImageChange} />
                            <div
                                className={`rounded-5 overflow-hidden position-relative d-flex align-items-center justify-content-center transition-all ${!generalInfo.heroPreview ? 'bg-light border-2 border-dashed' : 'shadow-lg'}`}
                                style={{ height: '400px' }}
                            >
                                {generalInfo.heroPreview ? (
                                    <>
                                        <img
                                            key={generalInfo.heroPreview} // Force re-render on new preview
                                            src={generalInfo.heroPreview}
                                            alt="Hero"
                                            className="w-100 h-100 object-fit-cover"
                                        />
                                        <div className="position-absolute bottom-0 end-0 m-3 transition-all d-flex gap-2">
                                            <button
                                                className="btn btn-warning rounded-pill px-3 py-2 fw-bold shadow-sm d-flex align-items-center gap-2"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleGenerateHeroShot();
                                                }}
                                                disabled={isOptimizing}
                                            >
                                                <Sparkles size={16} /> KI Neu
                                            </button>
                                            <span className="badge bg-dark bg-opacity-75 rounded-pill px-3 py-2 d-flex align-items-center">
                                                <Camera size={16} className="me-2" /> Bild ändern
                                            </span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-center text-muted">
                                        <Camera size={48} className="mb-3 opacity-25" />
                                        <h5 className="fw-bold font-sans">Titelbild hinzufügen</h5>
                                        <p className="small opacity-75 mb-3">Klicke hier um ein Foto hochzuladen</p>
                                        <button
                                            className="btn btn-warning rounded-pill px-3 py-2 fw-bold shadow-sm d-inline-flex align-items-center gap-2"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleGenerateHeroShot();
                                            }}
                                            disabled={isOptimizing}
                                        >
                                            <Sparkles size={16} /> KI Generieren
                                        </button>
                                    </div>
                                )}
                            </div>
                        </label>

                        {/* Title Input */}
                        <Form.Control
                            type="text"
                            placeholder="Rezept Titel"
                            className={`form-control-flush text-center display-4 fw-bold font-serif mb-3 ${errors.name ? 'border-danger text-danger' : 'text-dark'} ${isLocked ? 'bg-transparent' : ''}`}
                            value={generalInfo.name}
                            onChange={(e) => {
                                if (isLocked) return;
                                handleGeneralChange('name', e.target.value);
                                if (errors.name) setErrors(prev => ({ ...prev, name: false }));
                            }}
                            isInvalid={!!errors.name}
                            readOnly={isLocked}
                        />
                        {errors.name && (
                            <div className="text-danger small mb-3 animate-fade-in font-sans fw-bold">
                                Bitte gib deinem Rezept einen Namen
                            </div>
                        )}

                        {/* Description Input */}
                        <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Eine kurze, leckere Beschreibung..."
                            className="form-control-flush text-center lead font-sans text-muted"
                            value={generalInfo.description}
                            onChange={(e) => !isLocked && handleGeneralChange('description', e.target.value)}
                            readOnly={isLocked}
                        />
                        <div className="d-flex align-items-center justify-content-center gap-4 mt-3">
                            <div className="d-flex align-items-center gap-2">
                                <Users size={18} className="text-muted" />
                                <Form.Control
                                    type="number"
                                    min="1"
                                    placeholder="Portionen"
                                    className="bg-white border-0 shadow-sm text-center"
                                    style={{ width: '80px' }}
                                    value={generalInfo.servings}
                                    onChange={(e) => !isLocked && handleGeneralChange('servings', e.target.value)}
                                    readOnly={isLocked}
                                />
                                <span className="text-muted">Personen</span>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                <Clock size={18} className="text-muted" />
                                <Form.Control
                                    type="number"
                                    min="0"
                                    placeholder="Vor."
                                    className="bg-white border-0 shadow-sm text-center"
                                    style={{ width: '80px' }}
                                    value={generalInfo.prepTime}
                                    onChange={(e) => !isLocked && handleGeneralChange('prepTime', e.target.value)}
                                    readOnly={isLocked}
                                />
                                <span className="text-muted">Min (Vor.)</span>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                <Clock size={18} className="text-muted" />
                                <Form.Control
                                    type="number"
                                    min="0"
                                    placeholder="Koch"
                                    className="bg-white border-0 shadow-sm text-center"
                                    style={{ width: '80px' }}
                                    value={generalInfo.cookTime}
                                    onChange={(e) => !isLocked && handleGeneralChange('cookTime', e.target.value)}
                                    readOnly={isLocked}
                                />
                                <span className="text-muted">Min (Koch)</span>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                <ChefHat size={18} className="text-muted" />
                                <Form.Select
                                    className="bg-white border-0 shadow-sm text-center font-sans fw-medium"
                                    style={{ width: '100px', cursor: 'pointer' }}
                                    value={generalInfo.difficulty}
                                    onChange={(e) => !isLocked && handleGeneralChange('difficulty', e.target.value)}
                                    disabled={isLocked}
                                >
                                    <option value="easy">Einfach</option>
                                    <option value="medium">Mittel</option>
                                    <option value="hard">Schwer</option>
                                </Form.Select>
                            </div>
                        </div>

                        {/* Tag Input */}
                        <div className="d-flex flex-column align-items-center justify-content-center mt-4">
                            <div className="d-flex align-items-center gap-2 mb-2 w-50">
                                <Tag size={18} className="text-muted" />
                                <div className="position-relative w-100">
                                    <Form.Control
                                        id="tag-input"
                                        type="text"
                                        placeholder="Tags hinzufügen (Enter)..."
                                        className="form-control-flush text-center"
                                        onKeyDown={handleAddTag}
                                        disabled={isLocked}
                                    />
                                    <button
                                        className="btn btn-link position-absolute end-0 top-50 translate-middle-y p-0 text-muted hover-text-primary"
                                        onClick={(e) => handleAddTag({ ...e, type: 'click', target: { value: document.getElementById('tag-input').value } })}
                                        disabled={isLocked}
                                    >
                                        <Plus size={18} />
                                    </button>
                                </div>
                            </div>
                            <div className="d-flex flex-wrap gap-2 justify-content-center">
                                {generalInfo.tags.map((tag, i) => (
                                    <Badge
                                        key={i}
                                        bg="light"
                                        text="dark"
                                        className="border fw-normal d-flex align-items-center gap-1 px-2 py-1"
                                    >
                                        {tag}
                                        {!isLocked && (
                                            <X
                                                size={12}
                                                className="cursor-pointer hover-text-danger"
                                                onClick={() => handleRemoveTag(tag)}
                                            />
                                        )}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    </div>



                    {/* --- Global Ingredients Summary (Read Only) --- */}
                    <div className="mb-5 animate-fade-in">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <h5 className="font-serif fw-bold mb-0 d-flex align-items-center gap-2">
                                <Utensils size={20} className="text-primary" /> Alle Zutaten
                            </h5>
                            <Badge bg="light" text="dark" className="border fw-normal font-sans">
                                {steps.reduce((acc, s) => acc + s.ingredients.filter(i => i.name).length, 0)} Positionen
                            </Badge>
                        </div>
                        <div className="bg-white p-4 rounded-4 shadow-sm border-light">
                            {steps.some(s => s.ingredients.some(i => i.name)) ? (
                                <div className="d-flex flex-wrap gap-3">
                                    {steps.flatMap(s => s.ingredients).filter(i => i.name).map((ing, i) => (
                                        <div key={i} className="d-flex align-items-center gap-2 bg-light px-3 py-2 rounded-pill border hover-scale transition-transform cursor-default">
                                            <div className={`rounded-circle p-1 d-flex align-items-center justify-content-center bg-white shadow-sm text-primary`} style={{ width: 24, height: 24 }}>
                                                <CheckSquare size={12} />
                                            </div>
                                            <span className="fw-bold text-dark">{ing.amount} {ing.unit}</span>
                                            <span className="text-muted">{ing.name}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-5 border-2 border-dashed rounded-4 bg-light bg-opacity-50">
                                    <ChefHat size={32} className="text-muted opacity-25 mb-3" />
                                    <p className="text-muted mb-0 font-sans">Noch keine Zutaten.</p>
                                    <small className="text-muted opacity-75">Füge Zutaten in den Schritten unten hinzu.</small>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* --- Section 2: Steps (Sortable) --- */}
                    <div className="d-flex flex-column gap-5 mb-5 pb-5">
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={steps.map(s => s.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                {steps.map((step, index) => (
                                    <SortableStep
                                        key={step.id}
                                        step={step}
                                        index={index}
                                        steps={steps}
                                        updateStepText={updateStepText}
                                        removeStep={removeStep}
                                        handleStepMediaChange={handleStepMediaChange}
                                        handleGenerateStepImage={handleGenerateStepImage}
                                        updateIngredient={updateIngredient}
                                        removeIngredient={removeIngredient}
                                        addIngredientToStep={addIngredientToStep}
                                        insertStep={insertStep}
                                        updateDependency={updateDependency}
                                        updateStepDuration={updateStepDuration}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>

                        <div className="text-center py-4">
                            <button
                                className="btn btn-outline-primary rounded-pill px-4 py-2 fw-medium border-2 d-inline-flex align-items-center gap-2 hover-scale transition-transform"
                                onClick={addStep}
                            >
                                <Plus size={20} /> Weiteren Schritt hinzufügen
                            </button>
                        </div>
                    </div>

                </div>

                {/* Upgrade Modal */}
                <PlanSelectionModal
                    show={showUpgradeModal}
                    onHide={() => setShowUpgradeModal(false)}
                    token={token}
                />
            </div>
        </div>
    );
};

export default RecipeEditDialog;
